import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import {
  documentConfig,
  DocumentLabel,
  DocumentType,
  VariantType,
  permissionsMap,
  type DocumentCardByType,
} from '../documentCard/DocumentCard.types'
import type { DocumentPageRequest, IndicateurTbd, LocationRecherche } from './IndicateurTdb.types'
import { loadRecherche } from '@/features/services/recherche'
import { useAppStateStore } from '@/features/application'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { type PagedRequest } from '@/features/interface/recherches/storeRechercheBuilder'
import { navigateTo } from '@/router/navigateTo'
import { useAjoutDocumentAction } from '../documentCard/useAjoutDocumentAction'

export const useIndicateurTdbStore = defineStore('indicateurTdb', () => {
  const indicateurTdb = ref<IndicateurTbd>()
  const appState = useAppStateStore()

  const { executeAjoutAction } = useAjoutDocumentAction()

  const SLOW_THRESHOLD = 5000

  const initializeCards = (): DocumentCardByType => {
    return Object.values(DocumentType).reduce((acc, type) => {
      acc[type] = {
        type,
        permissions: permissionsMap[type],
        indicateurs: {
          primary: { count: 0, loading: false, slowLoading: false, error: false, titre: undefined },
          secondary: { count: 0, loading: false, slowLoading: false, error: false, titre: undefined },
        },
      }
      return acc
    }, {} as DocumentCardByType)
  }

  const documentCards = ref<DocumentCardByType>(initializeCards())

  const authorizedDocumentCards = computed(() => {
    return Object.values(documentCards.value)
      .filter((card) => appState.autoriseLecture(...card.permissions))
      .reduce((acc, card) => {
        acc[card.type] = card
        return acc
      }, {} as Partial<DocumentCardByType>)
  })

  function createIndicateur(label: DocumentLabel, query: object, typeRecherche: LocationRecherche) {
    return {
      label,
      pageRequest: { query } as DocumentPageRequest,
      typeRecherche,
    }
  }

  function getTdbIndicateur(): IndicateurTbd {
    const adherentStatus = appState.contextCourant === TypesContextesAutorisesEnum.Adherent
    const isAdherent = (status: boolean) => status

    return {
      DossierIndicateur1: createIndicateur(
        isAdherent(adherentStatus) ? DocumentLabel.DemandeAValider : DocumentLabel.DemandeAccepterRefuser,
        isAdherent(adherentStatus)
          ? { statuts: ['AReprendreAdherent'] }
          : { statuts: ['RefusAdherent', 'RepriseAdherent'] },
        { domaine: 'formations', module: 'demande' },
      ),
      DossierIndicateur2: createIndicateur(
        DocumentLabel.DossiersCompleter,
        { statuts: ['AttentePiecesInstruction', 'AttentePiecesReglement'] },
        { domaine: 'formations', module: 'dossier' },
      ),
      FactureIndicateur1: createIndicateur(
        DocumentLabel.NouvellesFactures,
        { statuts: ['EnCoursDAnalyse', 'Transmis'], periodeDateReception: 'DerniersTrenteJours' },
        { domaine: 'factures' },
      ),
      FactureIndicateur2: createIndicateur(
        DocumentLabel.FacturesRegulariser,
        { statuts: ['AttentePiecesReglement'] },
        { domaine: 'factures' },
      ),
      // Réservations de fonds (DRF)
      DRFIndicateur1: createIndicateur(
        DocumentLabel.DRFACompleter,
        { statuts: ['AttentePiecesInstruction'] },
        { domaine: 'drf', module: 'dossier' },
      ),
      DRFIndicateur2: createIndicateur(
        DocumentLabel.DRFAccordees,
        { statuts: ['Accorde'] },
        { domaine: 'drf', module: 'dossier' },
      ),
      // Demandes de remboursement (DR)
      DRIndicateur1: createIndicateur(
        DocumentLabel.DRATraiter,
        { statuts: ['Valide', 'AttentePieces'] },
        { domaine: 'dr', module: 'dossier' },
      ),
      DRIndicateur2: createIndicateur(
        DocumentLabel.ProformaAttenteFacture,
        { statuts: ['SaisieAdherent'] },
        { domaine: 'dr', module: 'proforma' },
      ),
    }
  }

  async function fetchIndicator(type: DocumentType, variant: VariantType) {
    if (!indicateurTdb.value) return

    const card = documentCards.value[type]
    if (!appState.autoriseLecture(...card.permissions)) return

    const config = documentConfig[type][variant]
    const indicator = card.indicateurs[variant]
    const tbd = indicateurTdb.value

    indicator.titre = config.getLabel(tbd)
    indicator.loading = true
    indicator.error = false
    indicator.slowLoading = false

    const slowTimer = setTimeout(() => {
      if (indicator.loading) indicator.slowLoading = true
    }, SLOW_THRESHOLD)

    try {
      const result = await loadRecherche<ReturnType<typeof config.getPageRequest>, DocumentPageRequest>(
        config.route,
        config.getPageRequest(tbd) as PagedRequest<DocumentPageRequest>,
        {
          sablier: false,
          dialogMode: 'silent',
          appCache: true,
          appCacheAddKey: [appState.contextCourant, appState.utilisateur?.emailAffichage],
          appCacheDurationEnSecondes: 300,
        },
      )
      indicator.count = result?.totalRecords ?? 0
    } catch {
      indicator.error = true
    } finally {
      clearTimeout(slowTimer)
      indicator.loading = false
      indicator.slowLoading = false
    }
  }

  async function loadData() {
    indicateurTdb.value = getTdbIndicateur()

    const activeCards = Object.values(authorizedDocumentCards.value)

    const tasks = activeCards.flatMap((card) => VariantType.map((variant) => fetchIndicator(card.type, variant)))

    await Promise.all(tasks)
  }

  async function redirectionDocument(type: DocumentType, variant: VariantType) {
    if (!indicateurTdb.value) return
    const config = documentConfig[type][variant]
    const selectedIndicator = indicateurTdb.value![config.labelKey]

    navigateTo({
      to: 'recherche',
      ...selectedIndicator.typeRecherche,
      query: selectedIndicator.pageRequest.query as Record<string, string>,
      orderBy: selectedIndicator.pageRequest.orderBy,
    })
  }

  const ajoutDocument = async (type: DocumentType) => {
    await executeAjoutAction(type)
  }

  watch(() => [appState.contextCourant, appState.utilisateur?.emailAffichage], loadData, { immediate: true })

  return {
    documentCards,
    authorizedDocumentCards,
    loadData,
    ajoutDocument,
    redirectionDocument,
    fetchIndicator,
  }
})
